import Image from "next/image";
import React, { useState } from "react";
import styled from "styled-components";
import { body1, heading5 } from "~/guidelines/Typography";
import IconMinus from "../_assets/investor-type-tabs/minus.svg";
import IconPlus from "../_assets/investor-type-tabs/plus.svg";

interface Tab {
  key: string;
  label: string;
  icon: any;
  text: string;
}

interface Props {
  tabs: Tab[];
}

const TabsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 20px;
`;

const TabButton = styled.button<{ $isActive: boolean }>`
  ${heading5}
  background: ${({ $isActive, theme }) =>
    $isActive ? theme.color.background.darkFirst : "#413D3A"};
  color: ${({ theme }) => theme.color.text.darkFirst};
  border: none;
  width: 100%;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: background 0.2s;
  justify-content: flex-start;
  gap: 16px;
`;

const TabContent = styled.div`
  ${body1}
  background: ${({ theme }) => theme.color.background.darkFirst};
  color: ${({ theme }) => theme.color.text.darkFirst};
  padding: ${({ theme }) => theme.spacing(5)};
`;

const AnimatedTabContent = styled.div<{ $open: boolean }>`
  overflow: hidden;
  max-height: ${({ $open }) => ($open ? "500px" : "0")};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition:
    max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.3s;
  will-change: max-height, opacity;
`;

const IconWrapper = styled.div`
  margin-top: 20px;
`;

const UpperIcon = styled.div`
  width: 28px;
  height: 12px;
  background-color: ${({ theme }) => theme.color.background.lightThird};
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  border-top: 2px solid black;
  border-left: 2px solid black;
  border-right: 2px solid black;
`;

const IconContainer = styled.div`
  box-sizing: border-box;
  width: 28px;
  height: 28px;
  background-color: white;
  position: relative;
  margin-bottom: 30px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  border: 2px solid black;

  > img,
  > svg {
    align-self: center;
    width: 16px;
    height: 16px;
    margin-bottom: 4px;
    z-index: 1;
  }
`;

const MobileInvestorTypeTabs: React.FC<Props> = ({ tabs }) => {
  const [openTab, setOpenTab] = useState<string | null>("fund");

  const handleTabClick = (key: string) => {
    setOpenTab((prev) => (prev === key ? null : key));
  };

  return (
    <TabsContainer>
      {tabs.map((tab) => (
        <div key={tab.key}>
          <TabButton
            $isActive={openTab === tab.key}
            onClick={() => handleTabClick(tab.key)}
            aria-expanded={openTab === tab.key}
            aria-controls={`mobile-tab-content-${tab.key}`}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <IconWrapper>
                <UpperIcon />
                <IconContainer>
                  <Image src={tab.icon} alt={tab.label} />
                </IconContainer>
              </IconWrapper>

              <span>{tab.label}</span>
            </div>

            <span style={{ marginLeft: "auto", fontSize: 28, fontWeight: 600 }}>
              {openTab === tab.key ? (
                <Image src={IconMinus} alt="" />
              ) : (
                <Image src={IconPlus} alt="" />
              )}
            </span>
          </TabButton>

          <AnimatedTabContent
            $open={openTab === tab.key}
            id={`mobile-tab-content-${tab.key}`}
          >
            <TabContent>{tab.text}</TabContent>
          </AnimatedTabContent>
        </div>
      ))}
    </TabsContainer>
  );
};

export default MobileInvestorTypeTabs;
